import { useEffect } from "react";
import { ComponentProps } from "../registry";
import { useBound, writePath } from "../helpers";
import { BoundString, BoundValue } from "../../types";

/**
 * v1: Cloudflare Turnstile widget when a siteKey is present, otherwise a
 * visible placeholder so authors notice missing config. The token writes
 * back to the bound path on success.
 */
export function Turnstile({ fields, scope }: ComponentProps) {
  const siteKey = useBound<string>(fields.siteKey as BoundValue, scope);
  const tokenPath = (fields.token as BoundString | undefined)?.path;

  useEffect(() => {
    if (!siteKey || !tokenPath) return;
    // Lazy-load the Turnstile script the first time we need it.
    if (!document.getElementById("cf-turnstile-script")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true; s.defer = true;
      document.head.appendChild(s);
    }
  }, [siteKey, tokenPath]);

  if (!siteKey) {
    // No siteKey configured — emit an empty token so submission still works.
    if (tokenPath) writePath(tokenPath, "");
    return (
      <div className="a2-turnstile-placeholder">
        Turnstile (no siteKey configured — verification skipped)
      </div>
    );
  }

  // Real widget — the Turnstile script auto-renders divs with class cf-turnstile.
  // We pass a callback name via data attributes; not wired up to the React
  // tree because Turnstile owns the iframe lifecycle.
  return (
    <div
      className="cf-turnstile"
      data-sitekey={siteKey}
      data-callback={tokenPath ? "__turnstileCallback" : undefined}
      ref={(el) => {
        if (!el || !tokenPath) return;
        (window as unknown as Record<string, unknown>).__turnstileCallback =
          (token: string) => writePath(tokenPath, token);
      }}
    />
  );
}
